/**
 * Smart Reminders Page
 * Context-aware reminders based on deadlines, client activity, and compliance status.
 */

import { useQuery } from '@tanstack/react-query';
import { Bell, Clock, AlertTriangle, CheckCircle, Calendar, Shield, FileText, Loader2, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

interface Reminder {
  id: string;
  type: 'deadline' | 'follow_up' | 'compliance' | 'document' | 'review';
  urgency: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  dueDate?: string;
  clientName?: string;
  daysUntilDue?: number;
}

const URGENCY_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400' },
  high: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400' },
  medium: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
  low: { bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', text: 'text-zinc-400' },
};

const TYPE_ICONS: Record<string, any> = {
  deadline: Clock,
  follow_up: Users,
  compliance: Shield,
  document: FileText,
  review: Calendar,
};

export default function SmartRemindersPage() {
  // Fetch deadlines
  const { data: deadlinesData, isLoading: deadlinesLoading } = useQuery({
    queryKey: ['deadlines'],
    queryFn: async () => { const r = await apiClient.get('/deadlines'); return r.data.data; },
  });

  // Fetch alerts
  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['manager-alerts'],
    queryFn: async () => { const r = await apiClient.get('/manager/alerts'); return r.data.data; },
  });

  // Fetch dashboard for client activity
  const { data: dashData } = useQuery({
    queryKey: ['firm-dashboard'],
    queryFn: async () => { const r = await apiClient.get('/firm/dashboard'); return r.data.data; },
  });

  const isLoading = deadlinesLoading || alertsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Build reminders from multiple sources
  const reminders: Reminder[] = [];
  const now = new Date();

  // 1. Deadline reminders
  const deadlines = deadlinesData?.deadlines || [];
  for (const d of deadlines) {
    const dueDate = new Date(d.dueDate);
    const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / 86400000);
    if (daysUntil < -30) continue; // Skip very old deadlines

    const urgency = daysUntil < 0 ? 'critical' : daysUntil <= 7 ? 'high' : daysUntil <= 14 ? 'medium' : 'low';
    // API returns `title`, `deadlineType` - use title for display, deadlineType as fallback
    const label = d.title || d.deadlineType || d.type || 'Deadline';
    reminders.push({
      id: `deadline-${d.clientName || 'all'}-${d.deadlineType || d.id}`,
      type: 'deadline',
      urgency,
      title: daysUntil < 0 ? `OVERDUE: ${label}` : `${label} due in ${daysUntil} days`,
      description: d.clientName || 'All clients',
      dueDate: d.dueDate,
      clientName: d.clientName,
      daysUntilDue: daysUntil,
    });
  }

  // 2. Alert-based reminders
  const alerts = alertsData?.alerts || [];
  for (const a of alerts) {
    reminders.push({
      id: a.id,
      type: a.type === 'at_risk' ? 'follow_up' : a.type === 'revenue_anomaly' ? 'review' : 'compliance',
      urgency: a.severity === 'critical' ? 'critical' : a.severity === 'warning' ? 'high' : 'medium',
      title: a.title,
      description: a.description,
      clientName: a.clientName,
    });
  }

  // 3. Inactive client reminders from dashboard
  const clients = dashData?.clients || [];
  for (const c of clients) {
    if (c.last_chat_at) {
      const days = Math.floor((now.getTime() - new Date(c.last_chat_at).getTime()) / 86400000);
      if (days >= 30 && days < 60) {
        reminders.push({
          id: `inactive-${c.id}`,
          type: 'follow_up',
          urgency: 'low',
          title: `${c.display_name} inactive for ${days} days`,
          description: 'Consider scheduling a check-in call',
          clientName: c.display_name,
        });
      }
    }
    // Missing documents reminder
    if (parseInt(c.document_count) === 0 && c.xero_org_id) {
      reminders.push({
        id: `no-docs-${c.id}`,
        type: 'document',
        urgency: 'medium',
        title: `No documents uploaded for ${c.display_name}`,
        description: 'Client has accounting connected but no documents on file',
        clientName: c.display_name,
      });
    }
  }

  // Sort by urgency
  const urgencyOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  reminders.sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency]);

  // Deduplicate
  const seen = new Set<string>();
  const uniqueReminders = reminders.filter(r => {
    const key = r.title + r.clientName;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const criticalCount = uniqueReminders.filter(r => r.urgency === 'critical').length;
  const highCount = uniqueReminders.filter(r => r.urgency === 'high').length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Bell}
        title="Smart Reminders"
        description="Context-aware reminders across deadlines, compliance, and client activity"
      />


      {/* Summary */}
      <div className="flex gap-4">
        {criticalCount > 0 && (
          <Badge variant="destructive" className="text-xs">{criticalCount} critical</Badge>
        )}
        {highCount > 0 && (
          <Badge variant="secondary" className="text-xs">{highCount} high priority</Badge>
        )}
        <Badge variant="outline" className="text-xs">{uniqueReminders.length} total reminders</Badge>
      </div>

      {/* Reminders List */}
      <div className="space-y-3">
        {uniqueReminders.length === 0 ? (
          <Card>
            <CardContent className="pt-12 pb-12 text-center text-muted-foreground">
              <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-500 opacity-50" />
              <p className="font-medium">All clear!</p>
              <p className="text-sm mt-1">No outstanding reminders at this time.</p>
            </CardContent>
          </Card>
        ) : (
          uniqueReminders.slice(0, 50).map(reminder => {
            const style = URGENCY_STYLES[reminder.urgency];
            const Icon = TYPE_ICONS[reminder.type] || Bell;

            return (
              <div
                key={reminder.id}
                className={cn('flex items-start gap-3 p-4 rounded-lg border', style.bg, style.border)}
              >
                <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', style.text)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium">{reminder.title}</p>
                    <Badge variant="outline" className={cn('text-[9px] shrink-0', style.text)}>{reminder.urgency}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{reminder.description}</p>
                  {reminder.dueDate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Due: {new Date(reminder.dueDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
